// Global Types
import type {
  $Translate,
} from 'common.language';

// Global Enums
import {
  ErrorKey,
} from 'common.enums';
import {
  CrewmemberAuthType,
} from 'common.data';

// Global Components
import * as React from 'react';
import GoogleLogin from 'react-google-login';
import FacebookLogin from 'react-facebook-login/dist/facebook-login-render-props';
import {
  IoLogoFacebook,
  IoLogoGoogle,
} from 'react-icons/io5';

// Helpers
import _ from 'lodash';
import ErrorLog from '@vroskus/library-sentry/dist/browser';
import {
  CustomError,
} from '@vroskus/library-error';
import {
  makeCode,
} from '@vroskus/library-helpers/dist/browser';
import config from '../../../config';

// Components
import Loader from '../../layout/Loader';

// Types
type $Props = $Translate & {
  onSuccessAction: (arg0: {
    token: string;
    type: CrewmemberAuthType;
  }) => unknown;
};

type $State = {
  loading: boolean;
};

class SocialAuthButtonsView extends React.Component<$Props, $State> {
  constructor(props: $Props) {
    super(props);

    this.state = {
      loading: false,
    };
  }

  async onFailure(errorKey: string, error: Error): Promise<void> {
    await this.setStateAsync({
      loading: false,
    });

    const errorMessage: string = _.get(
      error,
      'details',
      error ? makeCode(JSON.stringify(error)) : 'Unknown message',
    );

    const customError = new CustomError(
      errorMessage,
      errorKey,
      {
        data: {
          error,
        },
      },
    );

    ErrorLog.exception(customError);
  }

  setStateAsync(state: Partial<$State>): Promise<void> {
    return new Promise((resolve) => {
      this.setState(
        (pervState: $State) => ({
          ...pervState,
          ...state,
        }),
        resolve,
      );
    });
  }

  renderGoogleButton() {
    const {
      onSuccessAction,
      t,
    } = this.props;

    return (config.AUTH_GOOGLE_CLIENT_ID ? (
      <GoogleLogin
        clientId={config.AUTH_GOOGLE_CLIENT_ID}
        cookiePolicy={'single_host_origin'}
        icon={false}
        onFailure={(error) => this.onFailure(
          ErrorKey.googleError,
          error,
        )}
        onSuccess={async (response) => {
          await this.setStateAsync({
            loading: false,
          });

          const token: string | undefined = _.get(
            response,
            'accessToken',
          );

          if (typeof token === 'undefined') {
            const customError = new CustomError(
              'AccessToken not provided',
              ErrorKey.googleError,
              {
                data: {
                  response,
                },
              },
            );

            ErrorLog.exception(customError);

            return;
          }

          await onSuccessAction({
            token,
            type: CrewmemberAuthType.google,
          });
        }}
        render={(renderProps) => (
          <button
            className={'btn btn_google btn_outlined w-full'}
            onClick={async () => {
              await this.setStateAsync({
                loading: true,
              });

              renderProps.onClick();
            }}
            type={'button'}
          >
            <IoLogoGoogle className={'mr-2 my-auto'} />
            <span className={'mx-auto'}>
              {t('Sign in with Google')}
            </span>
          </button>
        )}
      />
    ) : null);
  }

  renderFacebookButton() {
    const {
      onSuccessAction,
      t,
    } = this.props;

    return (config.AUTH_FACEBOOK_APP_ID ? (
      <FacebookLogin
        appId={config.AUTH_FACEBOOK_APP_ID}
        callback={async (response) => {
          await this.setStateAsync({
            loading: false,
          });

          await onSuccessAction({
            token: response.accessToken,
            type: CrewmemberAuthType.facebook,
          });
        }}
        disableMobileRedirect
        fields={'name,email,picture'}
        isMobile={false}
        onFailure={(error) => this.onFailure(
          ErrorKey.facebookError,
          error,
        )}
        render={(renderProps) => (
          <button
            className={'btn btn_facebook btn_outlined w-full'}
            onClick={async () => {
              await this.setStateAsync({
                loading: true,
              });

              renderProps.onClick();
            }}
            type={'button'}
          >
            <IoLogoFacebook className={'mr-2 my-auto'} />
            <span className={'mx-auto'}>
              {t('Sign in with Facebook')}
            </span>
          </button>
        )}
        scope={'public_profile, email'}
      />
    ) : null);
  }

  render() {
    const {
      t,
    } = this.props;
    const {
      loading,
    } = this.state;

    return (
      <Loader loading={loading === true}>
        <div className={'mb-3'}>
          {t('By creating an account you will be able to manage your registration data and subscribe to regatta events')}
        </div>
        <div className={'mb-3'}>
          {this.renderGoogleButton()}
        </div>
        <div>
          {this.renderFacebookButton()}
        </div>
      </Loader>
    );
  }
}

export default SocialAuthButtonsView;
